import { useTranslation } from "react-i18next";
import CustomTable from "components/ReactTable/CustomTable";
import React, { useEffect, useMemo, useState } from "react";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { Button, Card, CardTitle, Container } from "reactstrap";
import { ErrorToaster } from "components/common/toast";
import handleError from "utils/handleError";
import { successToaster } from "components/common/toast";

import {
  GetSectionCategoriesAPI,
  DeleteSectionCategoriesAPI,
} from "network/categoryAPI";
import AddCategorySectionModal from "components/modal/AddCategorySectionModal";
import TableFilter from "components/TableFilter/TableFilter";

export default function CategorySection({ sectionData }) {
  const { t } = useTranslation("category");
  const [modalOpen, setModalOpen] = useState(false);
  const [category, setCategory] = useState([]);
  const [editCategory, setEditCategory] = useState(null);
  const [alert, setAlert] = useState(null);
  const [fetchTrigger, setFetchTrigger] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");

  const resetState = () => {
    setSearch("");
    setSort("");
    setCurrentPage(1);
    setCategory([]);
    setTotalPages(1);
    setEditCategory(null);
    setModalOpen(false);
    setAlert(null);
  };
  useEffect(() => {
    resetState();
  }, [sectionData]);

  const handleOpenModel = () => {
    setEditCategory(null);
    setModalOpen(true);
  };

  const handleEdit = (category) => {
    setEditCategory(category);
    setModalOpen(true);
  };

  const warningWithConfirmMessage = (id) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={t("confirm.title")}
        onConfirm={() => handleDelete(id)}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="success"
        cancelBtnBsStyle="danger"
        confirmBtnText={t("confirm.yes")}
        cancelBtnText={t("confirm.cancel")}
        showCancel
        btnSize=""
      >
        {t("list.confirmDelete")}
      </ReactBSAlert>
    );
  };
  const hideAlert = () => {
    setAlert(null);
  };
  const columns = useMemo(
    () => [
      {
        Header: t("list.colName"),
        accessor: "category_name",
        Cell: ({ row }) => `${row.original.name || "-"} `,
        width: 80,
      },
      {
        Header: t("list.colDescription"),
        accessor: "description",
        Cell: ({ value }) => value || "-",
      },

      {
        Header: t("list.colAction"),
        accessor: "action",
        width: 30,
        Cell: ({ row }) => (
          <div className="d-flex gap-3">
            <i
              className="tim-icons icon-pencil mr-3 text-warning pointer"
              onClick={() => handleEdit(row.original)}
            ></i>
            <i
              className="tim-icons icon-trash-simple text-danger pointer"
              onClick={() => warningWithConfirmMessage(row?.original?.uuid)}
            ></i>
          </div>
        ),
      },
    ],
    [t]
  );

  const pageSize = 10;
  const getCategory = async (page = 1) => {
    setIsLoading(true);
    try {
      const response = await GetSectionCategoriesAPI(
        sectionData?.uuid,
        page,
        pageSize,
        search,
        sort?.value
      );
      if (response.success) {
        setCategory(response?.data?.categories);
        setTotalPages(response?.data?.total_pages);
      }
    } catch (error) {
      ErrorToaster(handleError(error.status_code, error.message));
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async (id) => {
    try {
      const response = await DeleteSectionCategoriesAPI(sectionData?.uuid, id);
      if (response.success) {
        successToaster(t("list.deleted"));
        setCategory((prevCategories) =>
          prevCategories.filter((category) => category.uuid !== id)
        );
        hideAlert();
      }
    } catch (error) {
      ErrorToaster(handleError(error.status_code, error.message));
    }
  };
  useEffect(() => {
    setCurrentPage(1);
    setFetchTrigger((prev) => !prev);
  }, [search, sort]);
  useEffect(() => {
    getCategory(currentPage);
  }, [currentPage, fetchTrigger, sectionData]);
  return (
    <div className="content">
      {alert}
      <Container>
        <Card className="p-4">
          <div className="d-flex flex-wrap gap-16 justify-content-between mb-4 align-items-center">
            <CardTitle tag="h2" className="mb-0">
              {t("list.title")}
            </CardTitle>
            <Button
              className="text-capitalize"
              color="primary"
              onClick={handleOpenModel}
            >
              {t("list.add")}
            </Button>
          </div>
          <TableFilter
            search={search}
            setSearch={setSearch}
            sort={sort}
            setSort={setSort}
            showSearch
            showSort
          />

          <CustomTable
            columns={columns}
            data={category || []}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            showPagination={totalPages > 1}
            isLoading={isLoading}
            minWidth="600px"
          />
        </Card>
      </Container>
      {modalOpen && (
        <AddCategorySectionModal
          ModelOpen={modalOpen}
          setModalOpen={setModalOpen}
          setFetchTrigger={setFetchTrigger}
          editCategory={editCategory}
          sectionData={sectionData}
          setCategory={setCategory}
        />
      )}
    </div>
  );
}
